<template>
  <div class="table-responsive base-vertical-table">
    <table
      :style="styles"
      :class="classes"
      aria-label="Vertikale Tabelle"
    >
      <tbody>
        <template v-for="(field, index) in tableFields">
          <tr
            v-if="!field.hidden"
            :key="`${field.key}-${index}`"
            :class="field.class"
            :style="field.style"
          >
            <th
              v-if="field.label || field.icon"
              scope="row"
            >
              <BaseIcon
                v-if="field.icon"
                :icon="field.icon"
                :style="{
                  color: 'var(--company-main-primary)',
                }"
              />
              {{ field.label }}
            </th>
            <td
              v-if="hasSlot(field.key)"
              :class="['base-table--slot']"
            >
              <slot
                :name="field.key"
                :field="field"
              />
            </td>
            <template v-if="!hasSlot(field.key) && getItem(field) instanceof Array">
              <td class="list">
                <span
                  v-for="(item, inx) in getItem(field)"
                  :key="inx"
                  v-html="item"
                />
              </td>
            </template>
            <td
              v-else-if="!hasSlot(field.key)"
              v-html="getItem(field) || field.value"
            />
          </tr>
        </template>
      </tbody>
    </table>
  </div>
</template>

<script>
export default {
  name: 'BaseVerticalTable',
  props: {
    fields: {
      type: Array,
      required: true,
    },
    items: {
      type: [Array, Object],
      default: () => ([]),
    },
    list: {
      type: Boolean,
      default: false,
    },
    striped: {
      type: Boolean,
      default: false,
    },
    divider: {
      type: Boolean,
      default: false,
    },
    colGap: {
      type: [String, Number],
      default: '10px',
    },
    colType: {
      type: String,
      default: 'even',
      validator: (val) => ['even', 'auto'].includes(val),
    },
    rowGap: {
      type: [String, Number],
      default: '10px',
    },
    showEmpty: {
      type: Boolean,
      default: false,
    },
    valueAlign: {
      type: String,
      default: 'start',
      validator: (val) => ['start', 'center', 'end'].includes(val),
    },
  },
  computed: {
    styles() {
      return {
        '--col-gap': Number.isNaN(Number(this.colGap)) ? this.colGap : `${this.colGap}px`,
        '--row-gap': Number.isNaN(Number(this.rowGap)) ? this.rowGap : `${this.rowGap}px`,
        '--col-type': this.colType === 'even' ? '1fr' : 'auto',
        '--value-align': this.valueAlign,
      };
    },
    classes() {
      return {
        'base-table': true,
        [`--value-${this.valueAlign}`]: true,
        'base-table--borderless': !this.divider,
        'base-table-list': this.list,
        'base-table--striped': this.striped,
      };
    },
    tableFields() {
      return this.fields.map((field) => ({
        ...field,
        hidden: field.hidden !== undefined ? field.hidden : !this.isValid(this.getItem(field)),
      }));
    },
  },
  methods: {
    isValid(val) {
      if (this.showEmpty) return true;

      const invalid = [undefined, null, ''];
      const isArrayValid = val instanceof Array ? val.length : true;

      return !invalid.includes(val) && isArrayValid;
    },
    getItem(field) {
      return this.items instanceof Array && this.items.length
        ? this.items.map((x) => x[field.key])[0]
        : this.items?.[field.key] || field.value;
    },
    hasSlot(field) {
      return this.$scopedSlots[field] || this.$slots[field];
    },
  },
};
</script>

<style lang="scss" scoped>
.base-table {
  margin: 0;
  width: 100%;

  th {
    display: inline-flex;
    align-items: flex-start;
    height: 100%;
    gap: 0.625rem;

    .base-icon {
      height: 1.5rem;
    }
  }

  tr {
    display: grid;
    grid-template-columns: var(--col-type) 1fr;
    align-items: center;

    &:not(:last-child) {
      border-bottom: 1px solid #dee2e6;
    }

    > * {
      border: none;
      padding: var(--row-gap) var(--col-gap);

      &:first-child {
        padding-left: 0;
      }

      &:last-child {
        padding-right: 0;
        text-align: var(--value-align);
      }

      &.base-table--slot {
        padding: 0;
      }
    }
  }

  &--borderless {
    tr:not(:last-child) {
      border: none;
    }
  }

  &--striped {
    tr:nth-child(odd) {
      background-color: #f2f2f3;
    }
  }
}

.list {
    display: flex;
    flex-direction: column;
}
</style>
